import { Building, and, from, is } from '@the-real-insight/tri-model';
import { userAPI } from '../UserAPI';

export interface SiteAddress {
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    country?: unknown;
    lat?: number;
    lon?: number;
}

/**
 * A Mongo id in whatever shape it arrived in: a string, an ObjectId, `{ $oid }`, or a populated
 * document. The reference on a site can be any of them depending on who wrote it and how it was
 * serialised, and `String({ $oid })` is "[object Object]" — an id that matches nothing and sends
 * the caller quietly back to comparing addresses.
 */
export function entityIdAsString(id: any): string | undefined {
    if (id == null || id === '') return undefined;
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && '$oid' in id) return (id as { $oid: string }).$oid;
    if (typeof id === 'object' && '_id' in id) return entityIdAsString((id as { _id: unknown })._id);

    return String(id);
}

/**
 * The buildings a site has recorded, read by id.
 *
 * A site that names its buildings has answered the question already, and that answer beats any
 * address comparison: the link was written when the building was created or attached, while a
 * comparison has to guess whether "Straße" and "Str." are the same street. Only a site that names
 * none falls through to matching.
 */
export async function buildingsRecordedOnSite(site: any): Promise<any[]> {
    const ids = (Array.isArray(site?.buildings) ? site.buildings : [])
        .map((entry: any) => entityIdAsString(entry))
        .filter((id: string | undefined): id is string => !!id);

    if (ids.length === 0) {
        return [];
    }

    // Already hydrated — the caller holds the buildings, not just their ids.
    if (ids.every((id: string, index: number) => typeof site.buildings[index] === 'object' && site.buildings[index]?.street !== undefined)) {
        return site.buildings;
    }

    const rows = await userAPI.query(from(Building.type).where(is('_id').in(ids) as any));

    return Array.isArray(rows) ? rows : [];
}

/**
 * The buildings of a site: what it has recorded, and only failing that, what its address matches.
 *
 * The order is the point. Resolving by address on every load discards a link that was deliberately
 * written — a repaired site would go empty again on the next reload, and the repair would have to
 * be repeated for as long as the address comparison keeps failing.
 */
export async function buildingsForSite(site: any): Promise<any[]> {
    const recorded = await buildingsRecordedOnSite(site).catch((error) => {
        console.error('Could not read the buildings recorded on the site', error);
        return [] as any[];
    });

    if (recorded.length > 0) {
        return recorded;
    }

    return queryBuildingsForSite(site);
}

/**
 * Resolve the building for a site/address (read-only; never mints).
 *
 * Prefers the server matcher (POST /buildings/match: exact composite id →
 * canonical key → street number → geo, with a street+house-number safeguard).
 * When that endpoint is unavailable (not yet deployed → 404 → null) or finds
 * nothing, falls back to a direct EXACT lookup (street + house number + postal +
 * city). The fallback deliberately keeps the house number — it never uses the
 * no-house-number query that matched every footprint on a street (15+ results).
 *
 * Returns an array (0 or 1 entries) to preserve the call contract.
 */
export async function queryBuildingsForSite(site: SiteAddress): Promise<any[]> {
    const street = String(site.street ?? '').trim();
    const streetNumber = String(site.streetNumber ?? '').trim();
    const postalCode = String(site.postalCode ?? '').trim();
    const city = String(site.city ?? '').trim();

    if (!street || !postalCode || !city) {
        return [];
    }

    // Preferred path: the server matcher (robust to geocoder string variance).
    try {
        const matched = await userAPI.matchBuilding({ ...site, street, streetNumber, postalCode, city });
        if (matched) {
            return [matched];
        }
    } catch (error) {
        console.error('matchBuilding failed; falling back to direct lookup', error);
    }

    // Resilient fallback: direct EXACT lookup, works without /buildings/match.
    const byFlat = await userAPI.query(
        from(Building.type).where(
            and(
                is('street').equal(street),
                is('streetNumber').equal(streetNumber),
                is('postalCode').equal(postalCode),
                is('city').equal(city),
            ),
        ),
    );
    if (byFlat.length > 0) {
        return byFlat;
    }

    const byNested = await userAPI.query(
        from(Building.type).where(
            and(
                is('address.street').equal(street),
                is('address.streetNumber').equal(streetNumber),
                is('address.postalCode').equal(postalCode),
                is('address.city').equal(city),
            ),
        ),
    );
    if (byNested.length > 0) {
        return byNested;
    }

    // City-agnostic fallback: handles city naming variance (e.g., "Frankfurt" vs "Frankfurt am Main")
    const byFlatNoCity = await userAPI.query(
        from(Building.type).where(
            and(
                is('street').equal(street),
                is('streetNumber').equal(streetNumber),
                is('postalCode').equal(postalCode),
            ),
        ),
    );
    if (byFlatNoCity.length > 0) {
        return byFlatNoCity;
    }

    return userAPI.query(
        from(Building.type).where(
            and(
                is('address.street').equal(street),
                is('address.streetNumber').equal(streetNumber),
                is('address.postalCode').equal(postalCode),
            ),
        ),
    );
}
